import { useEffect, useRef, useState } from 'react';
import { Icon } from '../../components/Icon.tsx';
import { bookDemoUrl } from './contact.ts';

interface Plan {
  eyebrow: string;
  name: string;
  tagline: string;
  price: string;
  includes: string[];
  featured?: boolean;
  badge?: string;
}

// WapSign is the entry tool; WapMeet bundles it in; WapStat stands alone. The
// WapMeet + WapSign bundle is featured and placed in the MIDDLE of the row.
const PLANS: Plan[] = [
  {
    eyebrow: 'އެޕް',
    name: 'ވެޕްސައިން',
    tagline: 'ރަސްމީ ލިޔެކިޔުމަށް ކަރުދާހެއް ނެތި ސޮއިކުރުން',
    price: '999',
    includes: ['ވެޕްސައިން'],
  },
  {
    eyebrow: 'ބަންޑަލް',
    name: 'ވެޕްމީޓް + ވެޕްސައިން',
    tagline: 'ބައްދަލުވުންތަކާއި ސޮއިކުރުން އެއްކޮށް',
    price: '1,999',
    includes: ['ވެޕްމީޓް', 'ވެޕްސައިން'],
    featured: true,
    badge: 'އެންމެ އަގުހެޔޮ',
  },
  {
    eyebrow: 'އެޕް',
    name: 'ވެޕްސްޓެޓް',
    tagline: 'ބޯހިމެނުމާއި ދިރިހުރި ދަފްތަރު',
    price: '1,999',
    includes: ['ވެޕްސްޓެޓް'],
  },
];

function Price({ amount }: { amount: string }): JSX.Element {
  return (
    <div className="flex items-baseline gap-1.5" dir="ltr">
      <span className="text-sm font-medium text-muted-foreground">MVR</span>
      <span className="price-num">{amount}</span>
      <span className="text-sm text-muted-foreground">/ މަހަކަށް</span>
    </div>
  );
}

function IncludeList({ apps }: { apps: string[] }): JSX.Element {
  return (
    <ul className="mt-5 space-y-2">
      {apps.map((a) => (
        <li key={a} className="flex items-center gap-2 text-sm">
          <span className="shrink-0 text-[hsl(var(--primary))]">
            <Icon name="check" size={18} />
          </span>
          <span>{a}</span>
        </li>
      ))}
    </ul>
  );
}

function PlanCard({ p }: { p: Plan }): JSX.Element {
  const on = p.featured;
  return (
    <div className={`pricing-card ${on ? 'is-featured' : ''}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="chip-dv">{p.eyebrow}</span>
        {p.badge && (
          <span
            className="chip-warn"
            style={{ background: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))' }}
          >
            {p.badge}
          </span>
        )}
      </div>
      <h3 className="head-dv mt-3 text-xl leading-tight">{p.name}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.tagline}</p>
      <div className="mt-5">
        <Price amount={p.price} />
      </div>
      <IncludeList apps={p.includes} />
      <a
        href={bookDemoUrl}
        target="_blank"
        rel="noreferrer"
        className={`${on ? 'btn-primary' : 'btn-outline'} mt-6 w-full`}
      >
        ފެށޭ
      </a>
    </div>
  );
}

/**
 * A carousel over the plan cards: a snap scroller with arrow + dot controls
 * that page one card at a time. On desktop the scroller becomes a grid and the
 * controls hide (the CSS handles that). RTL-safe — paging uses scrollIntoView.
 */
function PlanCarousel({ plans }: { plans: Plan[] }): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onScroll = () => {
      const cards = el.querySelectorAll<HTMLElement>('.pricing-card');
      if (!cards.length) return;
      const step = cards[0]!.offsetWidth + 16; // card width + gap
      setActive(Math.max(0, Math.min(cards.length - 1, Math.round(Math.abs(el.scrollLeft) / step))));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const go = (i: number) => {
    const el = ref.current;
    if (!el) return;
    const cards = el.querySelectorAll<HTMLElement>('.pricing-card');
    const target = Math.max(0, Math.min(cards.length - 1, i));
    cards[target]?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
  };

  return (
    <>
      <div ref={ref} className="pricing-scroller mt-12">
        {plans.map((p) => (
          <PlanCard key={p.name} p={p} />
        ))}
      </div>
      <div className="pricing-controls">
        <button
          type="button"
          className="pricing-arrow"
          aria-label="ކުރީގެ ޕްލޭން"
          disabled={active === 0}
          onClick={() => go(active - 1)}
        >
          <Icon name="chevron_right" size={22} />
        </button>
        <div className="pricing-dots">
          {plans.map((p, i) => (
            <button
              key={p.name}
              type="button"
              aria-label={`ޕްލޭން ${i + 1}`}
              aria-current={i === active}
              className={`pricing-dot ${i === active ? 'is-active' : ''}`}
              onClick={() => go(i)}
            />
          ))}
        </div>
        <button
          type="button"
          className="pricing-arrow"
          aria-label="ދެން އޮތް ޕްލޭން"
          disabled={active === plans.length - 1}
          onClick={() => go(active + 1)}
        >
          <Icon name="chevron_left" size={22} />
        </button>
      </div>
    </>
  );
}

export function Pricing(): JSX.Element {
  return (
    <section id="pricing" className="scroll-mt-20">
      <div className="folio-wide py-20 text-center sm:py-28">
        <div className="reveal mx-auto max-w-2xl">
          <span className="chip-dv">އަގުތައް</span>
          <h2 className="head-dv mt-4 text-3xl sm:text-[2.6rem] sm:leading-[1.3]">
            ތިޔަ <span className="text-[hsl(var(--primary))]">ކައުންސިލަށް</span> ފެތޭ ޕްލޭން
          </h2>
          <p className="mt-4 leading-loose text-muted-foreground">
            ބޭނުންވާ ޓޫލުން ފަށައި، ފަހުން އިތުރުކުރެވޭ. ހުރިހާ ޕްލޭނަކަށްވެސް އެއް ލޮގިން، ދިވެހި ލިޔެކިޔުން
            އަދި ސަޕޯޓް ހިމެނޭ.
          </p>
        </div>

        {/* plans — carousel on phone/tablet, grid on desktop; the all-tools plan featured in the middle */}
        <div className="reveal text-start" style={{ '--reveal-delay': '120ms' } as React.CSSProperties}>
          <PlanCarousel plans={PLANS} />
        </div>

        <p className="mt-8 text-xs text-muted-foreground">އަގުތައް ދިވެހި ރުފިޔާއިން، މަހަކަށް.</p>
      </div>
    </section>
  );
}
